import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, Lock, Loader2, Crown, Trophy, RefreshCw } from "lucide-react";
import { apiGet, apiPost } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import MotionButton from "../components/motion/MotionButton";
import QuestionView from "../components/QuestionView";
import BookCTA from "../components/BookCTA";
import {
  Select, SelectTrigger, SelectContent, SelectItem, SelectValue,
} from "../components/ui/select";

const COUNT_OPTIONS = [5, 10, 20];
const PASS_PCT = 70;

function difficultyChip(d) {
  if (d === "easy") return "bg-brand-green/10 text-brand-green";
  if (d === "hard") return "bg-brand-red/10 text-brand-red";
  return "bg-brand-gold/15 text-brand-gold";
}

function HeroHeader({ overview }) {
  const total = overview?.total ?? 0;
  const free = overview?.free_quota ?? 30;
  return (
    <div className="mb-8">
      <div className="eyebrow">// Banca esclusiva</div>
      <h1 className="heading-display text-4xl sm:text-5xl text-brand-primary mt-1 flex items-center gap-3">
        <Sparkles className="h-8 w-8 text-brand-gold" />
        Domande stile SSM
      </h1>
      <p className="text-base text-brand-secondary mt-3 max-w-2xl leading-relaxed">
        Casi clinici originali ispirati al concorso{" "}
        <span className="text-brand-primary font-medium">SSM</span>{" "}
        (Scuole di Specializzazione in Medicina). Scenari realistici,
        5 opzioni, ragionamento basato su linee guida italiane ed europee.
      </p>
      <div className="mt-5 flex flex-wrap gap-2 text-xs">
        <span className="px-3 py-1.5 rounded-full bg-brand-surfaceElevated border border-brand-border text-brand-secondary">
          {total} domande totali
        </span>
        <span className="px-3 py-1.5 rounded-full bg-brand-green/10 text-brand-green border border-brand-green/30">
          {free} gratuite
        </span>
        <span className="px-3 py-1.5 rounded-full bg-brand-gold/15 text-brand-gold border border-brand-gold/30">
          Resto · Premium Plus
        </span>
      </div>
    </div>
  );
}

function PremiumPlusCallout({ onUpgrade }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="border border-brand-gold/40 bg-gradient-to-br from-brand-gold/10 via-brand-surface to-brand-green/8 rounded-2xl p-6 sm:p-8"
      data-testid="ssm-premium-callout"
    >
      <div className="flex items-start gap-4">
        <div className="h-12 w-12 rounded-xl bg-brand-gold/20 text-brand-gold flex items-center justify-center shrink-0">
          <Lock className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-brand-gold mb-2">// Sblocca tutto il banco SSM</div>
          <h3 className="heading-display text-2xl sm:text-3xl text-brand-primary">
            500 casi clinici, ogni specialità.
          </h3>
          <p className="mt-3 text-base text-brand-secondary leading-relaxed">
            Hai accesso a <span className="text-brand-primary font-medium">30 domande gratuite</span>.
            Con <span className="text-brand-gold font-medium">Premium Plus</span> a €22,49/mese
            studi tutte le 500 domande SSM-style, con filtri per specialità e difficoltà.
          </p>
          <MotionButton
            data-testid="ssm-upgrade-btn"
            variant="primary"
            className="mt-5"
            onClick={onUpgrade}
          >
            <Crown className="h-4 w-4" />
            Passa a Premium Plus
          </MotionButton>
        </div>
      </div>
    </motion.div>
  );
}

function SpecialtyGrid({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="mt-7">
      <div className="eyebrow mb-3">// Distribuzione per specialità</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div
            key={r.specialty}
            className="border border-brand-border bg-brand-surface rounded-xl px-4 py-3 flex items-center justify-between"
            data-testid={`ssm-specialty-${r.specialty}`}
          >
            <span className="text-sm text-brand-primary font-medium">{r.specialty}</span>
            <div className="flex items-center gap-1.5">
              <span className={`text-[10px] px-2 py-0.5 rounded ${difficultyChip("easy")}`}>F {r.easy}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded ${difficultyChip("medium")}`}>M {r.medium}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded ${difficultyChip("hard")}`}>D {r.hard}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SetupForm({ overview, onStart, starting }) {
  const [specialty, setSpecialty] = useState("all");
  const [difficulty, setDifficulty] = useState("all");
  const [count, setCount] = useState(10);
  const specialties = (overview?.by_specialty || []).map((r) => r.specialty);

  return (
    <div className="premium-card p-6 sm:p-8 mt-7">
      <div className="eyebrow">// Configura la sessione</div>
      <h2 className="font-serif text-2xl text-brand-primary mt-1">Inizia una sessione SSM</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        <div>
          <label className="eyebrow mb-2 block">Specialità</label>
          <Select value={specialty} onValueChange={setSpecialty}>
            <SelectTrigger data-testid="ssm-filter-specialty" className="bg-brand-background border-brand-border rounded-xl h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              {specialties.map((s) => (
                <SelectItem key={s} value={s}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="eyebrow mb-2 block">Difficoltà</label>
          <Select value={difficulty} onValueChange={setDifficulty}>
            <SelectTrigger data-testid="ssm-filter-difficulty" className="bg-brand-background border-brand-border rounded-xl h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutte</SelectItem>
              <SelectItem value="easy">Facile</SelectItem>
              <SelectItem value="medium">Media</SelectItem>
              <SelectItem value="hard">Difficile</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="eyebrow mb-2 block">Numero domande</label>
          <Select value={String(count)} onValueChange={(v) => setCount(Number(v))}>
            <SelectTrigger data-testid="ssm-filter-count" className="bg-brand-background border-brand-border rounded-xl h-11">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {COUNT_OPTIONS.map((n) => (
                <SelectItem key={n} value={String(n)}>{n} domande</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="mt-6">
        <MotionButton
          data-testid="ssm-start-btn"
          variant="primary"
          size="lg"
          disabled={starting}
          onClick={() => onStart({ specialty, difficulty, count })}
        >
          {starting ? <><Loader2 className="h-4 w-4 animate-spin" /> Caricamento</> : <><Sparkles className="h-4 w-4" /> Inizia sessione</>}
        </MotionButton>
      </div>
    </div>
  );
}

function SessionRunner({ session, onAnswered, onFinish }) {
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // {is_correct, specialty}
  const total = session.questions.length;
  const q = session.questions[idx];

  const handleAnswered = (r) => {
    const next = [...answers, { is_correct: r.is_correct, specialty: q.specialty }];
    setAnswers(next);
    onAnswered?.(r);
    if (idx + 1 >= total) {
      // Finish on last question
      setTimeout(() => onFinish(next), 0);
    }
  };

  const handleNext = () => {
    if (idx + 1 < total) setIdx(idx + 1);
    else onFinish(answers);
  };

  if (!q) return null;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="eyebrow">
          // Domanda {idx + 1} di {total}
        </div>
        <div className="flex items-center gap-2 text-xs text-brand-secondary">
          <span className="text-brand-green font-medium">
            {answers.filter((a) => a.is_correct).length}
          </span>
          <span>/</span>
          <span>{answers.length}</span>
          <span>corrette</span>
        </div>
      </div>
      <div className="h-1.5 bg-brand-surfaceElevated rounded-full overflow-hidden mb-6">
        <motion.div
          className="h-full bg-brand-gold"
          initial={{ width: 0 }}
          animate={{ width: `${((idx) / total) * 100}%` }}
          transition={{ duration: 0.4 }}
        />
      </div>
      <QuestionView
        key={q.question_id}
        question={q}
        mode="training"
        onAnswered={handleAnswered}
        onNext={handleNext}
        showNext
      />
    </div>
  );
}

function ResultsView({ session, answers, onRestart }) {
  const total = answers.length;
  const correct = answers.filter((a) => a.is_correct).length;
  const score = total ? Math.round((correct / total) * 100) : 0;
  const passed = score >= PASS_PCT;
  const accent = passed ? "border-brand-green/40 bg-brand-green/5" : "border-brand-red/40 bg-brand-red/5";
  const scoreColor = passed ? "text-brand-green" : "text-brand-red";

  // breakdown by specialty
  const bySpecialty = {};
  for (const a of answers) {
    const k = a.specialty || "Generale";
    if (!bySpecialty[k]) bySpecialty[k] = { correct: 0, total: 0 };
    bySpecialty[k].total += 1;
    if (a.is_correct) bySpecialty[k].correct += 1;
  }

  return (
    <div>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className={`border-2 rounded-3xl p-8 sm:p-10 text-center ${accent}`}
      >
        <Trophy className={`h-12 w-12 mx-auto mb-4 ${scoreColor}`} />
        <div className="eyebrow">// Risultato sessione SSM</div>
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 18, delay: 0.3 }}
          className={`heading-display text-7xl sm:text-8xl mt-4 ${scoreColor}`}
          data-testid="ssm-score"
        >
          {score}%
        </motion.div>
        <p className="mt-4 text-base text-brand-secondary">
          {correct} risposte corrette su {total}.
        </p>
        <div className="mt-7 flex flex-wrap gap-3 justify-center">
          <MotionButton data-testid="ssm-restart-btn" variant="primary" onClick={onRestart}>
            <RefreshCw className="h-4 w-4" />
            Nuova sessione
          </MotionButton>
        </div>
      </motion.div>

      {!passed && (
        <div className="mt-6">
          <BookCTA />
        </div>
      )}

      {Object.keys(bySpecialty).length > 0 && (
        <div className="mt-6 premium-card p-6">
          <div className="eyebrow mb-3">// Performance per specialità</div>
          <div className="space-y-2">
            {Object.entries(bySpecialty).map(([s, v]) => (
              <div
                key={s}
                className="flex justify-between border border-brand-border bg-brand-background rounded-xl px-4 py-3"
              >
                <span className="text-sm text-brand-primary">{s}</span>
                <span className="font-serif text-base font-semibold text-brand-primary">
                  {v.correct}/{v.total}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!session.is_premium_plus && (
        <div className="mt-6">
          <PremiumPlusCallout onUpgrade={() => (window.location.href = "/premium")} />
        </div>
      )}
    </div>
  );
}

export default function SSM() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [session, setSession] = useState(null);
  const [answers, setAnswers] = useState(null); // null = running, array = finished
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    apiGet("/ssm/overview").then(setOverview).catch(() => setOverview({ total: 0, by_specialty: [], free_quota: 30 }));
  }, []);

  const startSession = useCallback(async ({ specialty, difficulty, count }) => {
    setStarting(true);
    setError(null);
    try {
      const params = { count };
      if (specialty !== "all") params.specialty = specialty;
      if (difficulty !== "all") params.difficulty = difficulty;
      const r = await apiGet("/ssm/session", params);
      if (!r.questions?.length) {
        setError("Nessuna domanda disponibile per questi filtri.");
        return;
      }
      setSession(r);
      setAnswers(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento sessione.");
    } finally {
      setStarting(false);
    }
  }, []);

  const finish = useCallback((finalAnswers) => {
    setAnswers(finalAnswers);
  }, []);

  const restart = useCallback(() => {
    setSession(null);
    setAnswers(null);
  }, []);

  const isPP = overview?.is_premium_plus || user?.subscription_level === "premium_plus";

  return (
    <div className="px-6 sm:px-10 py-8 lg:py-12 max-w-4xl mx-auto" data-testid="ssm-page">
      <HeroHeader overview={overview} />

      {!session && (
        <>
          {!isPP && overview?.total > 0 && (
            <div className="mb-6">
              <PremiumPlusCallout onUpgrade={() => navigate("/premium")} />
            </div>
          )}
          <SpecialtyGrid rows={overview?.by_specialty} />
          <SetupForm overview={overview} onStart={startSession} starting={starting} />
          {error && (
            <div className="mt-4 text-sm text-brand-red border border-brand-red/30 bg-brand-red/5 rounded-xl px-4 py-3">
              {error}
            </div>
          )}
        </>
      )}

      {session && answers === null && (
        <SessionRunner session={session} onFinish={finish} />
      )}

      {session && answers !== null && (
        <ResultsView session={session} answers={answers} onRestart={restart} />
      )}
    </div>
  );
}
